import { ChangeDetectionStrategy, Component, computed, DestroyRef, Inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FilterableItemsListComponent, FilterableListItemDirective } from '@examdojo/core/filterable-items-list';
import { TranslocoModule } from '@jsverse/transloco';
import { switchMap, tap } from 'rxjs';
import { IconComponent } from '@examdojo/core/icon';
import { ENTITY_TAG_REPOSITORY, EntityTagRepository } from '../../data/entity-tag-repository';
import { TagEntityQuery } from '../../data/tag-entity.query';
import { TAG_REPOSITORY, TagRepository } from '../../data/tag-repository';
import { Tag } from '../../data/tag.model';
import { TagBadgeComponent } from '../tag-badge/tag-badge.component';
import { TagEditorComponent, TagEditorData } from '../tag-editor/tag-editor.component';

type ViewState = 'list' | 'create' | 'edit';

@Component({
  selector: 'dojo-tag-dropdown',
  templateUrl: './tag-dropdown.component.html',
  imports: [
    FilterableItemsListComponent,
    FilterableListItemDirective,
    TagBadgeComponent,
    TagEditorComponent,
    TranslocoModule,
    IconComponent,
  ],
  host: { class: 'block' },
  styles: [
    `
      :host ::ng-deep {
        .item .mat-mdc-checkbox .mdc-label {
          width: calc(100% - 24px);
        }

        .item .mat-mdc-checkbox .mdc-checkbox {
          --mdc-checkbox-state-layer-size: 20px;
          margin-top: -2px;
        }

        .items-wrapper {
          .mat-mdc-menu-item:hover:not([disabled]) {
            @apply bg-neutral-50;
          }
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagDropdownComponent {
  constructor(
    @Inject(TAG_REPOSITORY) private readonly tagRepository: TagRepository,
    @Inject(ENTITY_TAG_REPOSITORY) private readonly entityTagRepository: EntityTagRepository,
    private readonly tagEntityQuery: TagEntityQuery,
    private readonly destroyRef: DestroyRef,
  ) {}

  readonly entityId = input.required<string | number>();
  readonly createTagName = signal('');
  readonly viewState = signal<ViewState>('list');
  readonly editingTag = signal<Tag | null>(null);

  private readonly entityTags = toSignal(
    toObservable(this.entityId).pipe(switchMap((id) => this.tagEntityQuery.selectTagsForEntity(id))),
    { initialValue: [] as Tag[] },
  );

  readonly selectedIds = computed(() => this.entityTags().map((t) => t.id));

  readonly tags = toSignal(this.tagRepository.selectAll(), { initialValue: [] as Tag[] });

  readonly identityFn = (tag: Tag): string => tag.id;
  readonly filterPropertyValueFn = (tag: Tag): string => tag.name;

  onSelectionChange(newSelectedIds: string[]): void {
    const entityId = this.entityId();
    const currentSet = new Set(this.selectedIds());
    const newSet = new Set(newSelectedIds);

    const addedId = newSelectedIds.find((id) => !currentSet.has(id));
    if (addedId) {
      this.entityTagRepository.assign(entityId, addedId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      return;
    }

    const removedId = this.selectedIds().find((id) => !newSet.has(id));
    if (removedId) {
      this.entityTagRepository.remove(entityId, removedId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  onCreateItem(name: string): void {
    this.createTagName.set(name);
    this.viewState.set('create');
  }

  openEditView(tag: Tag): void {
    this.editingTag.set(tag);
    this.viewState.set('edit');
  }

  onCreate(data: TagEditorData): void {
    this.tagRepository
      .create(data)
      .pipe(
        switchMap((newTag) => this.entityTagRepository.assign(this.entityId(), newTag.id)),
        tap(() => this.resetToListView()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onUpdate(data: TagEditorData): void {
    const tag = this.editingTag();
    if (!tag) {
      return;
    }

    this.tagRepository
      .update({ ...tag, ...data })
      .pipe(
        tap(() => this.resetToListView()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onDelete(): void {
    const tag = this.editingTag();
    if (!tag) {
      return;
    }

    this.tagRepository
      .delete(tag.id)
      .pipe(
        tap(() => this.resetToListView()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private resetToListView(): void {
    this.viewState.set('list');
    this.editingTag.set(null);
    this.createTagName.set('');
  }
}
